import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faX } from '@fortawesome/free-solid-svg-icons';
import { faCircleCheck } from '@fortawesome/free-regular-svg-icons';

interface StaffProfileCardProps {
  id: string;
  candidateName: string;
  title: string;
  experience: string;
  verification: string;
  skills: string[];
  industry: string;
  industries?: string[];
  summary?: string;
  email?: string;
  phone?: string;
  linkedInLink?: string;
  dayRate?: number;
  dayRateCurrency?: string;
  onBookmark: () => void;
  isBookmarked?: boolean;
  profileData?: any; // Full profile data to pass to details page
  onViewProfile?: (profileData: any) => void; // Callback to open drawer
  hideShortlistButton?: boolean; // Hide the shortlist button
}

const StaffProfileCard: React.FC<StaffProfileCardProps> = ({
  id,
  candidateName,
  title,
  experience,
  verification,
  skills,
  industry,
  industries,
  summary,
  email,
  phone,
  linkedInLink,
  dayRate,
  dayRateCurrency,
  onBookmark,
  isBookmarked = false,
  profileData,
  onViewProfile,
  hideShortlistButton = false
}) => {
  return (
    <div
      className="rounded-lg p-6 transition-all duration-200 hover:scale-105"
      style={{
        backgroundColor: '#FFFFFF',
        border: `1px solid ${isBookmarked ? '#FF5C3E' : 'rgba(0, 52, 230, 0.25)'}`,
        fontFamily: 'var(--dreamdev-font-family)',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
      }}
      onMouseEnter={(e) => (e.target as HTMLElement).style.borderColor = isBookmarked ? '#FF5C3E' : '#6BCEFF'}
      onMouseLeave={(e) => (e.target as HTMLElement).style.borderColor = isBookmarked ? '#FF5C3E' : 'rgba(0, 52, 230, 0.25)'}
    >
      {/* Two-column layout */}
      <div className="flex flex-col gap-4 lg:flex-row lg:gap-6">
        {/* Left column - All content except SEE PROFILE button */}
        <div className="flex-1">
          <div className="flex flex-col sm:flex-row sm:items-center mb-2 space-y-2 sm:space-y-0">
            <h3
              className="sm:mr-4"
              style={{
                fontSize: '18px',
                color: '#03238C',
                fontWeight: '700',
                fontFamily: 'Inter, sans-serif',
                letterSpacing: '-0.04em'
              }}
            >
              {candidateName}
            </h3>
            <div className="flex items-center">
              <FontAwesomeIcon
                icon={faCircleCheck}
                className="w-4 h-4 mr-1"
                style={{ color: '#0034E6' }}
              />
              <span
                style={{
                  color: '#0034E6',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: 'Inter, sans-serif'
                }}
              >
                {verification}
              </span>
            </div>
          </div>

          {/* Role/Title */}
          <div className="mb-2">
            <span
              style={{
                color: '#6B7280',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              {title}
            </span>
          </div>

          <p
            className="text-sm mb-4"
            style={{
              color: '#6B7280',
              fontSize: 'var(--dreamdev-font-size-sm)'
            }}
          >
            {experience}
          </p>

          {/* Contact Information */}
          <div className="mb-4 space-y-1">
            {email && (
              <div className="text-sm" style={{ color: '#374151' }}>
                <span className="font-medium">Email:</span> {email}
              </div>
            )}
            {phone && (
              <div className="text-sm" style={{ color: '#374151' }}>
                <span className="font-medium">Phone:</span> {phone}
              </div>
            )}
            {linkedInLink && (
              <div className="text-sm">
                <a
                  href={linkedInLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#0034E6', textDecoration: 'underline' }}
                >
                  LinkedIn Profile
                </a>
              </div>
            )}
            {dayRate && (
              <div className="text-sm" style={{ color: '#374151' }}>
                <span className="font-medium">Daily Rate:</span> {dayRateCurrency || 'USD'} {dayRate.toLocaleString()}
              </div>
            )}
          </div>

          {/* About content */}
          {summary && (
            <div className="mb-4">
              <p
                className="text-sm leading-relaxed"
                style={{
                  color: '#374151',
                  fontSize: 'var(--dreamdev-font-size-sm)',
                  fontFamily: 'var(--dreamdev-font-family)',
                  display: '-webkit-box',
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {summary}
              </p>
            </div>
          )}

          {/* Skills */}
          <div className="mb-4">
            <div className="flex flex-wrap gap-2">
              {skills.slice(0, 6).map((skill, index) => (
                <span
                  key={index}
                  className="px-2 py-1 rounded-full"
                  style={{
                    backgroundColor: '#E5F3FF',
                    color: '#03238C',
                    fontSize: '12px',
                    fontWeight: '500',
                    fontFamily: 'Source Sans 3, sans-serif'
                  }}
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>

          {/* Industries tags */}
          <div className="flex flex-wrap gap-2 mb-4">
            {industries && industries.length > 0 ? (
              industries.map((industry, index) => (
                <span
                  key={index}
                  className="px-3 py-1 rounded-full"
                  style={{
                    backgroundColor: '#CEECFF',
                    color: '#03238C',
                    fontSize: '14px',
                    fontWeight: '400',
                    fontFamily: 'Source Sans 3, sans-serif'
                  }}
                >
                  {industry}
                </span>
              ))
            ) : (
              <span
                className="px-3 py-1 rounded-full"
                style={{
                  backgroundColor: '#CEECFF',
                  color: '#03238C',
                  fontSize: '14px',
                  fontWeight: '400',
                  fontFamily: 'Source Sans 3, sans-serif'
                }}
              >
                {industry}
              </span>
            )}
          </div>

          {/* ADD TO SHORTLIST button - bottom left */}
          {!hideShortlistButton && (
            <div className="flex justify-start">
              <button
                onClick={onBookmark}
                className="px-4 py-2 text-sm uppercase flex items-center justify-center space-x-2 transition-colors duration-200 w-full sm:w-auto"
                style={{
                  backgroundColor: 'rgba(255, 92, 62, 0.1)',
                  color: '#FF5C3E',
                  fontSize: 'var(--dreamdev-font-size-sm)',
                  fontWeight: '700',
                  borderRadius: '6px',
                  border: '1px solid #FF5C3E',
                  textShadow: 'none'
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255, 92, 62, 0.2)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255, 92, 62, 0.1)';
                }}
              >
                <FontAwesomeIcon
                  icon={isBookmarked ? faX : faPlus}
                  className="w-3 h-3"
                />
                <span>{isBookmarked ? 'REMOVE FROM SHORTLIST' : 'ADD TO SHORTLIST'}</span>
              </button>
            </div>
          )}
        </div>

        {/* Right column - Only SEE PROFILE button */}
        <div className="flex justify-start lg:justify-end lg:items-start">
          <button
            onClick={() => onViewProfile?.(profileData)}
            className="px-4 py-2 text-sm font-bold uppercase flex items-center justify-center space-x-2 transition-colors duration-200 w-full lg:w-auto"
            style={{
              backgroundColor: '#E9F7FF',
              color: '#03238C',
              fontSize: 'var(--dreamdev-font-size-sm)',
              fontWeight: '700',
              borderRadius: '6px',
              textDecoration: 'none',
              border: 'none',
              cursor: 'pointer'
            }}
            onMouseEnter={(e: any) => {
              e.currentTarget.style.backgroundColor = '#D1EFFF';
            }}
            onMouseLeave={(e: any) => {
              e.currentTarget.style.backgroundColor = '#E9F7FF';
            }}
          >
            <span>VIEW DETAILS</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default StaffProfileCard;
